import { DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ShowLobbyService } from '../../services/show-lobby.service';
import { PreparedShow, PreparedShowQuestion, PreparedShowStatus, QuestionLimitMode, QuestionUsageMode, ShowFormatOption } from '../../models/show-lobby.model';

import { appEnvironment } from '../../app-environment';

type LobbyStatusFilter = 'all' | PreparedShowStatus;
type LobbyTimeFilter = 'all' | 'past' | 'today' | 'future';

interface QuestionCandidate extends PreparedShowQuestion {
  format: string;
}

@Component({
  selector: 'app-show-lobby',
  standalone: true,
  imports: [FormsModule, RouterLink, DatePipe],
  template: `
    <section class="section show-lobby-page">
      <div class="page-title-block show-lobby-heading">
        <h1>SHOW LOBBY</h1>
        <span class="title-line"></span>
      </div>

      @if (auth.canUseCreatorSpace()) {
        <section class="show-lobby-top-grid">
          <article class="card accent-card join-lobby-card wide-join-card">
            <div class="lobby-card-head compact-head">
              <div>
                <small>Aktiver Code</small>
                <h2>Show beitreten</h2>
              </div>
            </div>

            <form class="join-code-form wide-join-form" (ngSubmit)="joinLobby()">
              <label>
                <span>Lobby Code</span>
                <input type="text" [(ngModel)]="joinCode" name="joinCode" placeholder="ABC-123" />
              </label>
              <button class="button primary" type="submit">Beitreten</button>
            </form>

            @if (joinedShow()) {
              <div class="joined-show-box compact-result">
                <small>Gefunden</small>
                <h3>{{ joinedShow()!.title }}</h3>
                <p>{{ formatTitle(joinedShow()!.format) }} · {{ joinedShow()!.date | date:'dd.MM.yyyy' }}</p>
                <button class="button primary compact-button" type="button" (click)="enterJoinedLobby()">Lobby öffnen</button>
              </div>
            } @else if (joinMessage()) {
              <p class="lobby-message error">{{ joinMessage() }}</p>
            }
          </article>

          <article class="card accent-card active-code-list active-shows-card">
            <small>Aktive Shows</small>
            @for (show of activeShows(); track show.id) {
              <div>
                <span>{{ show.title }}</span>
                <strong>{{ show.lobbyCode }}</strong>
              </div>
            } @empty {
              <p>Noch keine aktive Show.</p>
            }
          </article>
        </section>

        <section class="show-lobby-command-row">
          <button class="button primary" type="button" (click)="openCreateModal()">Neue Show erstellen</button>
          @if (saveMessage()) {
            <p class="lobby-message inline-message">{{ saveMessage() }}</p>
          }
          @if (errorMessage()) {
            <p class="lobby-message error inline-message">{{ errorMessage() }}</p>
          }
          @if (isLoading()) {
            <p class="lobby-message inline-message">Backend lädt...</p>
          }
        </section>

        <section class="show-lobby-filter-block card accent-card">
          <label>
            <span>Format</span>
            <select [(ngModel)]="selectedFormat" name="selectedFormat">
              <option value="all">Alle</option>
              @for (format of lobby.formats; track format.id) {
                <option [value]="format.id">{{ format.title }}</option>
              }
            </select>
          </label>

          <label>
            <span>Zeitraum</span>
            <select [(ngModel)]="selectedTime" name="selectedTime">
              <option value="all">Alle</option>
              <option value="past">Vergangen</option>
              <option value="today">Heute</option>
              <option value="future">Zukunft</option>
            </select>
          </label>

          <label>
            <span>Status</span>
            <select [(ngModel)]="selectedStatus" name="selectedStatus">
              <option value="all">Alle</option>
              <option value="draft">Entwurf</option>
              <option value="active">Aktiv</option>
              <option value="finished">Beendet</option>
            </select>
          </label>
        </section>

        <section class="show-status-tabs">
          <button type="button" [class.active]="selectedStatus === 'draft'" (click)="selectedStatus = 'draft'">Entwurf</button>
          <button type="button" [class.active]="selectedStatus === 'active'" (click)="selectedStatus = 'active'">Aktiv</button>
          <button type="button" [class.active]="selectedStatus === 'finished'" (click)="selectedStatus = 'finished'">Beendet</button>
          <button type="button" [class.active]="selectedStatus === 'all'" (click)="selectedStatus = 'all'">Alle</button>
        </section>

        <section class="prepared-show-list">
          @for (show of filteredShows; track show.id) {
            <article class="prepared-show-card card accent-card" [class.active-show]="show.status === 'active'" [class.finished-show]="show.status === 'finished'">
              <div class="prepared-main">
                <span class="show-status-pill" [class.status-draft]="show.status === 'draft'" [class.status-active]="show.status === 'active'" [class.status-finished]="show.status === 'finished'">
                  {{ statusLabel(show.status) }}
                </span>
                <h2>{{ show.title }}</h2>
                <p>{{ formatTitle(show.format) }} · {{ show.date | date:'dd.MM.yyyy' }} · Host: {{ show.host }}</p>
              </div>

              <div class="prepared-meta prepared-meta-expanded">
                <div><span>Kategorien</span><strong>{{ show.selectedCategories.length ? show.selectedCategories.join(', ') : 'Alle' }}</strong></div>
                <div><span>Schwierigkeit</span><strong>{{ show.selectedDifficulties.length ? show.selectedDifficulties.join(', ') : 'Alle' }}</strong></div>
                <div><span>Fragen</span><strong>{{ questionUsageLabel(show) }}</strong></div>
                <div><span>Fragenlimit</span><strong>{{ questionLimitLabel(show) }}</strong></div>
                <div><span>Ausgewählt</span><strong>{{ show.selectedQuestions.length }} Fragen</strong></div>
                <div><span>Lobby Code</span><strong>{{ show.status === 'active' ? show.lobbyCode : '—' }}</strong></div>
                <div><span>Aktualisiert</span><strong>{{ show.updatedAt | date:'dd.MM.yyyy HH:mm' }}</strong></div>
              </div>

              <div class="prepared-actions">
                @if (show.status === 'active') {
                  <button class="button primary compact-button" type="button" (click)="enterLobby(show)">Lobby öffnen</button>
                }
                <button class="button secondary compact-button" type="button" (click)="openEditModal(show)">Bearbeiten</button>
                @if (show.status === 'draft') {
                  <button class="button primary compact-button" type="button" (click)="activate(show.id)">Aktivieren</button>
                }
                @if (show.status === 'active') {
                  <button class="button secondary compact-button" type="button" (click)="finish(show.id)">Beenden</button>
                }
              </div>
            </article>
          } @empty {
            <article class="card accent-card no-access-card empty-show-card"><h2>Keine Shows gefunden</h2></article>
          }
        </section>

        @if (isEditorOpen()) {
          <div class="show-lobby-modal-backdrop" (click)="closeEditor()">
            <article class="show-lobby-modal card accent-card" (click)="$event.stopPropagation()">
              <div class="modal-head">
                <div>
                  <small>{{ editingShowId() ? 'Entwurf aktualisieren' : 'Neue Show erstellen' }}</small>
                  <h2>{{ editingShowId() ? 'Show bearbeiten' : 'Show vorbereiten' }}</h2>
                </div>
                <button class="modal-close" type="button" (click)="closeEditor()">×</button>
              </div>

              <form class="show-lobby-form modal-show-form" (ngSubmit)="saveDraft()">
                <label>
                  <span>Format</span>
                  <select [(ngModel)]="form.format" name="format" (ngModelChange)="onFormatChanged()">
                    @for (format of lobby.formats; track format.id) {
                      <option [value]="format.id">{{ format.title }}</option>
                    }
                  </select>
                </label>

                <label><span>Titel</span><input type="text" [(ngModel)]="form.title" name="title" placeholder="z. B. 50 Fragen zu Gaming" /></label>
                <label><span>Datum</span><input type="date" [(ngModel)]="form.date" name="date" /></label>
                <label><span>Host</span><input type="text" [(ngModel)]="form.host" name="host" placeholder="DaniiPlace" /></label>

                <fieldset class="show-filter-field full-field">
                  <legend>Category</legend>
                  <div class="choice-grid">
                    <button type="button" class="choice-chip" [class.active]="isAllCategoriesSelected()" (click)="selectAllCategories()">Alle</button>
                    @for (category of selectedFormatOption().categories; track category) {
                      <button type="button" class="choice-chip" [class.active]="isCategorySelected(category)" (click)="toggleCategory(category)">{{ category }}</button>
                    }
                  </div>
                </fieldset>

                <fieldset class="show-filter-field full-field">
                  <legend>Difficulty</legend>
                  <div class="choice-grid">
                    <button type="button" class="choice-chip" [class.active]="isAllDifficultiesSelected()" (click)="selectAllDifficulties()">Alle</button>
                    @for (difficulty of selectedFormatOption().difficulties; track difficulty) {
                      <button type="button" class="choice-chip" [class.active]="isDifficultySelected(difficulty)" (click)="toggleDifficulty(difficulty)">{{ difficulty }}</button>
                    }
                  </div>
                </fieldset>

                <fieldset class="show-filter-field full-field">
                  <legend>Fragen-Auswahl</legend>
                  <div class="usage-toggle-grid">
                    <button type="button" class="choice-chip big-choice" [class.active]="form.questionUsageMode === 'unused_only'" (click)="setQuestionUsageMode('unused_only')">Nur nicht verwendete Fragen</button>
                    <button type="button" class="choice-chip big-choice" [class.active]="form.questionUsageMode === 'include_played'" (click)="setQuestionUsageMode('include_played')">Gespielte Fragen erlauben</button>
                    <button type="button" class="choice-chip big-choice" [class.active]="form.questionUsageMode === 'custom_questions'" (click)="setQuestionUsageMode('custom_questions')">Eigene Fragen</button>
                  </div>

                  @if (form.questionUsageMode === 'custom_questions') {
                    <p class="helper-text">Diese Show nutzt eigene Fragen und schreibt sie später nicht automatisch in den allgemeinen Fragenpool.</p>
                  }

                  @if (form.questionUsageMode === 'include_played') {
                    <div class="range-grid">
                      <label>
                        <span>Gespielte Fragen maximal</span>
                        <strong>{{ form.playedQuestionPercentage ?? 0 }}%</strong>
                        <input type="range" min="0" max="100" step="5" [(ngModel)]="form.playedQuestionPercentage" name="playedQuestionPercentage" (ngModelChange)="regenerateQuestions()" />
                      </label>
                      <label>
                        <span>Falsch beantwortet ↔ richtig beantwortet</span>
                        <strong>{{ form.playedCorrectnessTarget ?? 50 }}%</strong>
                        <input type="range" min="0" max="100" step="5" [(ngModel)]="form.playedCorrectnessTarget" name="playedCorrectnessTarget" (ngModelChange)="regenerateQuestions()" />
                      </label>
                    </div>
                  }
                </fieldset>

                <fieldset class="show-filter-field full-field">
                  <legend>Fragen-Begrenzer</legend>
                  <div class="usage-toggle-grid">
                    <button type="button" class="choice-chip big-choice" [class.active]="form.questionLimitMode === 'limited'" (click)="setQuestionLimitMode('limited')">Begrenzt</button>
                    <button type="button" class="choice-chip big-choice" [class.active]="form.questionLimitMode === 'unlimited'" (click)="setQuestionLimitMode('unlimited')">Unlimitiert</button>
                  </div>

                  @if (form.questionLimitMode === 'limited') {
                    <label class="inline-number-field">
                      <span>Maximale Fragen</span>
                      <input type="number" min="1" step="1" [(ngModel)]="form.questionLimitCount" name="questionLimitCount" placeholder="50" (ngModelChange)="regenerateQuestions()" />
                    </label>
                  } @else {
                    <p class="helper-text">Keine feste Obergrenze. Im Frontend werden zur Vorschau trotzdem die passenden Fragen angezeigt.</p>
                  }
                </fieldset>

                <label class="full-field notes-field">
                  <span>Notizen</span>
                  <textarea rows="5" [(ngModel)]="form.notes" name="notes" placeholder="Interne Vorbereitung, Gäste, Ablauf, Besonderheiten..."></textarea>
                </label>

                <section class="generated-questions-panel full-field">
                  <div class="generated-questions-head">
                    <div>
                      <small>{{ form.questionUsageMode === 'custom_questions' ? 'Eigene Fragen' : 'Automatisch generiert' }}</small>
                      <h3>Fragen für diese Show</h3>
                    </div>
                    <div class="generated-actions">
                      <span>{{ preparedQuestions.length }} Fragen</span>
                      @if (form.questionUsageMode === 'custom_questions') {
                        <button class="button secondary compact-button" type="button" (click)="addCustomQuestion()">Eigene Frage hinzufügen</button>
                      }
                    </div>
                  </div>

                  <div class="generated-question-list">
                    @for (question of preparedQuestions; track question.id; let i = $index) {
                      <article class="generated-question-card">
                        <div class="generated-question-meta">
                          <strong>#{{ i + 1 }}</strong>
                          <span>{{ question.category }}</span>
                          <span>{{ question.difficulty }}</span>
                          @if (question.alreadyPlayed) {
                            <span>{{ question.wasAnsweredCorrectly ? 'richtig' : 'falsch' }} · {{ question.hitRatePercent ?? 0 }}%</span>
                          } @else {
                            <span>nicht verwendet</span>
                          }
                        </div>

                        <label>
                          <span>Frage</span>
                          <textarea rows="2" [(ngModel)]="question.questionText" [name]="'preparedQuestionText_' + question.id"></textarea>
                        </label>

                        <label>
                          <span>Lösung</span>
                          <input type="text" [(ngModel)]="question.solutionText" [name]="'preparedQuestionSolution_' + question.id" />
                        </label>

                        <button class="question-remove-button" type="button" (click)="removePreparedQuestion(question.id)">Entfernen</button>
                      </article>
                    } @empty {
                      <article class="generated-empty-state">
                        <h3>Noch keine Fragen</h3>
                        <p>Bei eigenen Fragen kannst du manuell Fragen hinzufügen. Bei Fragenpool-Formaten erscheinen hier passende Fragen anhand deiner Filter.</p>
                      </article>
                    }
                  </div>
                </section>

                <div class="show-lobby-actions full-field modal-actions">
                  <button class="button secondary" type="button" (click)="closeEditor()">Schließen</button>
                  <button class="button secondary" type="submit">Zwischenspeichern</button>
                  <button class="button primary" type="button" (click)="saveAndActivate()">Speichern & aktivieren</button>
                </div>
              </form>
            </article>
          </div>
        }
      } @else {
        <article class="card accent-card no-access-card">
          <h2>Kein Zugriff</h2>
          <p>Show Lobby ist für Admin/Creator-Rollen vorbereitet.</p>
          <a routerLink="/login" class="button primary">Einloggen</a>
        </article>
      }
    </section>
  `
})
export class ShowLobbyComponent implements OnInit {
  readonly auth = inject(AuthService);
  readonly lobby = inject(ShowLobbyService);
  readonly router = inject(Router);

  readonly saveMessage = signal('');
  readonly joinMessage = signal('');
  readonly joinedShow = signal<PreparedShow | null>(null);
  readonly editingShowId = signal<string | null>(null);
  readonly isEditorOpen = signal(false);
  readonly isLoading = signal(false);
  readonly errorMessage = signal('');
  readonly activeShows = computed(() => this.lobby.shows().filter(show => show.status === 'active'));

  joinCode = '';
  selectedFormat = 'all';
  selectedTime: LobbyTimeFilter = 'all';
  selectedStatus: LobbyStatusFilter = 'draft';

  form: Partial<PreparedShow> = this.createEmptyForm();
  preparedQuestions: PreparedShowQuestion[] = [];

  get filteredShows(): PreparedShow[] {
    return this.lobby.shows()
      .filter(show => this.selectedFormat === 'all' || show.format === this.selectedFormat)
      .filter(show => this.selectedStatus === 'all' || show.status === this.selectedStatus)
      .filter(show => this.matchesTimeFilter(show))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  ngOnInit(): void {
    this.loadShows();
  }

  loadShows(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.lobby.loadShows().subscribe({
      next: () => this.isLoading.set(false),
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set(`Shows konnten nicht vom Backend geladen werden. Läuft das Backend auf ${appEnvironment.backendLabel}?`);
      }
    });
  }

  openCreateModal(): void {
    this.editingShowId.set(null);
    this.form = this.createEmptyForm();
    this.preparedQuestions = [];
    this.regenerateQuestions();
    this.saveMessage.set('');
    this.errorMessage.set('');
    this.isEditorOpen.set(true);
  }

  openEditModal(show: PreparedShow): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.lobby.getShow(show.id).subscribe({
      next: detail => {
        this.isLoading.set(false);
        this.editingShowId.set(detail.id);
        this.form = { ...detail, selectedQuestions: [...detail.selectedQuestions] };
        this.preparedQuestions = detail.selectedQuestions?.length ? [...detail.selectedQuestions] : [];
        if (!this.preparedQuestions.length) {
          this.regenerateQuestions();
        }
        this.saveMessage.set('');
        this.isEditorOpen.set(true);
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Show konnte nicht vom Backend geladen werden.');
      }
    });
  }

  closeEditor(): void {
    this.isEditorOpen.set(false);
  }

  saveDraft(): void {
    this.form.selectedQuestions = this.preparedQuestions;
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.lobby.saveDraft(this.form).subscribe({
      next: saved => {
        this.isLoading.set(false);
        this.editingShowId.set(saved.id);
        this.form = { ...saved };
        this.preparedQuestions = [...saved.selectedQuestions];
        this.saveMessage.set('Show wurde im Backend zwischengespeichert.');
        this.closeEditor();
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Show konnte nicht gespeichert werden.');
      }
    });
  }

  saveAndActivate(): void {
    this.form.selectedQuestions = this.preparedQuestions;
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.lobby.saveAndActivate(this.form).subscribe({
      next: activated => {
        this.isLoading.set(false);
        this.editingShowId.set(activated.id);
        this.form = { ...activated };
        this.preparedQuestions = [...activated.selectedQuestions];
        this.saveMessage.set(`Show ist aktiv. Code: ${activated.lobbyCode}`);
        this.selectedStatus = 'active';
        this.closeEditor();
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Show konnte nicht aktiviert werden.');
      }
    });
  }

  activate(showId: string): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.lobby.activateShow(showId).subscribe({
      next: activated => {
        this.isLoading.set(false);
        this.saveMessage.set(`Show ist aktiv. Code: ${activated.lobbyCode}`);
        this.selectedStatus = 'active';
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Show konnte nicht aktiviert werden.');
      }
    });
  }

  finish(showId: string): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.lobby.finishShow(showId).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.saveMessage.set('Show wurde beendet. Der Lobby-Code ist nicht mehr aktiv.');
        this.selectedStatus = 'finished';
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Show konnte nicht beendet werden.');
      }
    });
  }

  joinLobby(): void {
    this.joinedShow.set(null);
    this.joinMessage.set('');
    this.errorMessage.set('');

    const displayName = this.auth.activeUser().username === 'Gast' ? 'Gast' : this.auth.activeUser().username;
    this.lobby.joinByCode(this.joinCode, displayName).subscribe({
      next: show => {
        this.joinedShow.set(show);
        this.joinMessage.set('');
        this.router.navigate(['/show-lobby', show.id]);
      },
      error: () => {
        this.joinedShow.set(null);
        this.joinMessage.set('Keine aktive Show mit diesem Code gefunden.');
      }
    });
  }


  enterJoinedLobby(): void {
    const show = this.joinedShow();
    if (!show) return;
    this.enterLobby(show);
  }

  enterLobby(show: PreparedShow): void {
    this.router.navigate(['/show-lobby', show.id]);
  }

  onFormatChanged(): void {
    const format = this.selectedFormatOption();
    this.form.selectedCategories = this.filterExistingSelection(this.form.selectedCategories ?? [], format.categories);
    this.form.selectedDifficulties = this.filterExistingSelection(this.form.selectedDifficulties ?? [], format.difficulties);
    this.preparedQuestions = [];
    this.regenerateQuestions();
  }

  selectedFormatOption(): ShowFormatOption {
    return this.lobby.getFormat(this.form.format ?? this.lobby.formats[0].id);
  }

  selectAllCategories(): void { this.form.selectedCategories = []; this.regenerateQuestions(); }
  selectAllDifficulties(): void { this.form.selectedDifficulties = []; this.regenerateQuestions(); }

  toggleCategory(category: string): void {
    const current = this.form.selectedCategories ?? [];
    this.form.selectedCategories = current.includes(category) ? current.filter(item => item !== category) : [...current, category];
    this.regenerateQuestions();
  }

  toggleDifficulty(difficulty: string): void {
    const current = this.form.selectedDifficulties ?? [];
    this.form.selectedDifficulties = current.includes(difficulty) ? current.filter(item => item !== difficulty) : [...current, difficulty];
    this.regenerateQuestions();
  }

  isCategorySelected(category: string): boolean { return (this.form.selectedCategories ?? []).includes(category); }
  isDifficultySelected(difficulty: string): boolean { return (this.form.selectedDifficulties ?? []).includes(difficulty); }
  isAllCategoriesSelected(): boolean { return (this.form.selectedCategories ?? []).length === 0; }
  isAllDifficultiesSelected(): boolean { return (this.form.selectedDifficulties ?? []).length === 0; }

  setQuestionUsageMode(mode: QuestionUsageMode): void {
    this.form.questionUsageMode = mode;

    if (mode === 'unused_only' || mode === 'custom_questions') {
      this.form.playedQuestionPercentage = 0;
    } else {
      this.form.playedQuestionPercentage ??= 20;
      this.form.playedCorrectnessTarget ??= 50;
    }

    this.preparedQuestions = [];
    this.regenerateQuestions();
  }

  setQuestionLimitMode(mode: QuestionLimitMode): void {
    this.form.questionLimitMode = mode;

    if (mode === 'unlimited') {
      this.form.questionLimitCount = null;
    } else {
      this.form.questionLimitCount ??= 50;
    }

    this.regenerateQuestions();
  }

  regenerateQuestions(): void {
    if (this.form.questionUsageMode === 'custom_questions') {
      if (!this.preparedQuestions.length || !this.preparedQuestions.every(question => question.isCustom)) {
        this.preparedQuestions = [this.createCustomQuestion()];
      }
      return;
    }

    const existingEdits = new Map(this.preparedQuestions.map(question => [question.sourceQuestionId ?? question.id, question]));
    this.lobby.previewQuestions(this.form).subscribe({
      next: questions => {
        this.preparedQuestions = questions.map(question => {
          const edited = existingEdits.get(question.sourceQuestionId ?? question.id);
          return edited
            ? { ...question, questionText: edited.questionText, solutionText: edited.solutionText }
            : question;
        });
      },
      error: () => {
        this.errorMessage.set('Fragenvorschau konnte nicht vom Backend geladen werden.');
        this.preparedQuestions = [];
      }
    });
  }

  addCustomQuestion(): void {
    this.preparedQuestions = [...this.preparedQuestions, this.createCustomQuestion()];
  }

  removePreparedQuestion(questionId: string): void {
    this.preparedQuestions = this.preparedQuestions.filter(question => question.id !== questionId);
  }

  questionUsageLabel(show: PreparedShow): string {
    if (show.questionUsageMode === 'custom_questions') return 'Eigene Fragen';
    if (show.questionUsageMode === 'unused_only') return 'Nur nicht verwendete';
    return `${show.playedQuestionPercentage}% gespielt erlaubt`;
  }

  questionLimitLabel(show: PreparedShow): string {
    return show.questionLimitMode === 'unlimited' ? 'Unlimitiert' : `${show.questionLimitCount ?? 50} Fragen`;
  }

  formatTitle(formatId: string): string { return this.lobby.formats.find(format => format.id === formatId)?.title ?? formatId; }

  statusLabel(status: PreparedShowStatus): string {
    const labels: Record<PreparedShowStatus, string> = { draft: 'Entwurf', active: 'Aktiv', finished: 'Beendet' };
    return labels[status];
  }

  private matchesTimeFilter(show: PreparedShow): boolean {
    if (this.selectedTime === 'all') return true;
    const today = new Date().toISOString().slice(0, 10);
    if (this.selectedTime === 'past') return show.date < today;
    if (this.selectedTime === 'today') return show.date === today;
    return show.date > today;
  }

  private createEmptyForm(): Partial<PreparedShow> {
    const firstFormat = this.lobby.formats[0];
    return {
      title: '',
      format: firstFormat.id,
      date: new Date().toISOString().slice(0, 10),
      host: this.auth.activeUser().username === 'Gast' ? 'DaniiPlace' : this.auth.activeUser().username,
      notes: '',
      selectedCategories: [],
      selectedDifficulties: [],
      questionUsageMode: 'unused_only',
      playedQuestionPercentage: 0,
      playedCorrectnessTarget: 50,
      questionLimitMode: 'limited',
      questionLimitCount: 50,
      selectedQuestions: []
    };
  }

  private filterExistingSelection(selection: string[], allowedValues: string[]): string[] {
    return selection.filter(value => allowedValues.includes(value));
  }

  private createCustomQuestion(): PreparedShowQuestion {
    const id = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    return {
      id,
      sourceQuestionId: null,
      poolId: this.lobby.getPoolIdForFormat(this.form.format ?? this.lobby.formats[0].id),
      questionText: '',
      solutionText: '',
      category: this.form.selectedCategories?.[0] ?? 'Eigene Frage',
      difficulty: this.form.selectedDifficulties?.[0] ?? 'custom',
      alreadyPlayed: false,
      wasAnsweredCorrectly: null,
      hitRatePercent: null,
      isCustom: true,
      data: {}
    };
  }
}
